import { css, html, LitElement, unsafeCSS } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import snarkdown from "../../libs/snarkdown";
import { sendQuestion } from "../../services/chatbot.service";
import styles from "./chat-float.css?inline";
import { CHAT_BOT_SRC } from "../../constants/urls.constant";
import {
  iconChevronDown,
  iconClose,
  iconEmail,
  iconPhone,
  iconSend,
  iconWhatsapp,
} from "../../icons";
import { LocalStorage } from "../../constants/local-storage.constant";
import {
  isBrowser,
  getWindow,
  getLocalStorage,
  runInBrowser,
} from "../../utils/dom-utils";
import { CURRENT_VERSION } from "../../constants/configurations";

type ChatState = "init" | "active" | "timeout";

@customElement("chat-float")
export class ChatFloat extends LitElement {
  // Propiedades importantes
  @property({ type: String, attribute: "unique-organization-token" })
  uniqueOrganizationToken = "";
  @property({ type: String, attribute: "zindex" }) zindex = "9999";
  @property({ type: String, attribute: "img" }) avatarSrc = CHAT_BOT_SRC;
  @property({ type: Number, attribute: "timeout-seconds" })
  timeoutSeconds = 120;
  @property({ type: String, attribute: "email" }) email?: string;
  @property({ type: String, attribute: "phone" }) phone?: string;
  @property({ type: String, attribute: "whatsapp" }) whatsapp?: string;

  // Textos
  @property({ type: String, attribute: "name" }) name = "GIA";
  @property({ type: String }) welcome = "Hola <span>Soy el Bot</span>";
  @property({ type: String, attribute: "welcome-message" }) welcomeMessage =
    "¿En qué puedo ayudarte?";
  @property({ type: String, attribute: "placeholder" }) placeholder =
    "Como los contacto?";
  @property({ type: String, attribute: "instant-messages" }) instantMessages =
    '["¿Qué servicios tiene?", "Ok, gracias", "¿Cómo los contacto?"]';
  @property({ type: String, attribute: "text-loading" }) textLoading =
    "Escribiendo...";
  @property({ type: String, attribute: "end-text" }) endText =
    "Gracias por tu tiempo";
  @property({ type: String, attribute: "end-message" }) endMessage =
    "Si necesitas más información puedes volver aquí cuándo quieras. ¡Estamos para ayudarte!. También pueden contactarnos directamente:";
  @property({ type: String, attribute: "end-message-bottom" })
  endMessageBottom =
    "Te invito a explorar más aquí: <a href='https://globals.one/contact-us/' target='_blank'>Acerca de nosotros</a> ¡Que tengas excelente día! 🚀";
  @property({ type: String, attribute: "end-button" }) endButton =
    "Hablemos ahora";

  // Colores
  @property({ type: String, attribute: "primary-color" })
  primaryColor = "#0066FF";
  @property({ type: String, attribute: "secondary-color" }) secondaryColor =
    "#00FFC6";
  @property({ type: String, attribute: "background-bubble" }) backgroundBubble =
    "#0066FF";
  @property({ type: String, attribute: "background-chat" }) backgroundChat =
    "#0066FF";

  // Estados internos
  @state() private isOpen = false;
  @state() private closing = false;
  @state() private inputText = "";
  @state() private messages: Array<{
    from: "user" | "bot" | "error";
    text: string;
  }> = [];
  @state() private chatState: ChatState = "init";
  @state() private isLoading = false;

  @query("#bottom-anchor")
  private bottomAnchor!: HTMLDivElement;

  static styles = css`
    ${unsafeCSS(styles)}
  `;

  get isEmpty() {
    return this.messages.length === 0;
  }

  private timeoutId: number | null = null;
  private _onClickOutside = (e: MouseEvent) => {
    const path = e.composedPath();
    if (!path.includes(this)) {
      this.closing = true;
      setTimeout(() => {
        this.isOpen = false;
        this.closing = false;
      }, 100);
    }
  };

  private _onEscapePress = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.isOpen) {
      this.closing = true;
      setTimeout(() => {
        this.isOpen = false;
        this.closing = false;
      }, 100);
    }
  };

  private scrollToBottom(smooth = true) {
    if (!isBrowser) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        this.bottomAnchor?.scrollIntoView({
          behavior: smooth ? "smooth" : "auto",
          block: "nearest",
        });
      }, 10);
    });
  }

  private saveMessagesToStorage() {
    if (!isBrowser) return;

    try {
      const storage = getLocalStorage();
      if (storage) {
        storage.setItem(
          LocalStorage.CHAT_FLOAT_STORAGE_KEY,
          JSON.stringify(this.messages)
        );
      }
    } catch (e) {
      console.warn("No se pudo guardar el historial del chat", e);
    }
  }

  private loadMessagesFromStorage() {
    if (!isBrowser) return;

    try {
      const storage = getLocalStorage();
      if (storage) {
        const stored = storage.getItem(LocalStorage.CHAT_FLOAT_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            this.messages = parsed;
            if (parsed.length > 0) {
              this.chatState = "active";
            }
          }
        }
      }
    } catch (e) {
      console.warn("No se pudo cargar el historial del chat", e);
    }
  }

  startTimeout() {
    if (!isBrowser) return;

    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.timeoutId = window.setTimeout(() => {
      this.chatState = "timeout";
    }, this.timeoutSeconds * 1000);
  }

  resetTimeout() {
    if (!isBrowser) return;

    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.startTimeout();
  }

  toggleOpen() {
    this.resetTimeout();
    if (this.isOpen) {
      this.closing = true;
      setTimeout(() => {
        this.isOpen = false;
        this.closing = false;
      }, 100);
    } else {
      this.isOpen = true;

      if (isBrowser) {
        requestAnimationFrame(() => {
          const anchor = this.renderRoot.querySelector("#bottom-anchor");
          if (anchor) {
            anchor.scrollIntoView({ behavior: "smooth" });
          }
        });
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    runInBrowser(() => {
      this.loadMessagesFromStorage();
      const window = getWindow();
      if (window) {
        window.addEventListener("mousedown", this._onClickOutside);
        window.addEventListener("keydown", this._onEscapePress);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    runInBrowser(() => {
      const window = getWindow();
      if (window) {
        window.removeEventListener("mousedown", this._onClickOutside);
        window.removeEventListener("keydown", this._onEscapePress);
      }
    });
  }

  private async handleSendMessage(text: string) {
    if (!text.trim()) return;
    this.messages = [...this.messages, { from: "user", text }];
    this.inputText = "";
    this.resetTimeout();
    this.isLoading = true;

    const param = {
      message: text,
      uniqueOrganizationToken: this.uniqueOrganizationToken,
    };

    let botMessage = "";

    try {
      await sendQuestion(param, (chunk, success) => {
        if (!success) {
          this.messages = [...this.messages, { from: "error", text: chunk }];
          this.isLoading = false;
          return;
        }

        botMessage += chunk;
        const lastMessage = this.messages[this.messages.length - 1];
        if (lastMessage?.from === "bot") {
          this.messages = [
            ...this.messages.slice(0, -1),
            { from: "bot", text: botMessage },
          ];
        } else {
          this.messages = [...this.messages, { from: "bot", text: botMessage }];
        }
      });
    } catch (e) {
      console.error(e);
      this.messages = [
        ...this.messages,
        { from: "bot", text: "Ha ocurrido un error. Intenta más tarde." },
      ];
    } finally {
      this.isLoading = false;
    }
  }

  async sendQuick(text: string) {
    if (this.chatState === "init") this.chatState = "active";
    this.resetTimeout();
    await this.handleSendMessage(text);
  }

  async sendMessage() {
    const text = this.inputText.trim();
    if (!text) return;

    if (this.chatState === "init") this.chatState = "active";
    this.resetTimeout();
    await this.handleSendMessage(text);
  }

  willUpdate(changedProps: Map<string, unknown>) {
    if (changedProps.has("messages")) {
      this.scrollToBottom(true);
      this.saveMessagesToStorage();
    }
  }

  updated() {
    this.style.setProperty("--chat-zindex", this.zindex);
    this.style.setProperty("--chat-primary", this.primaryColor);
    this.style.setProperty("--chat-secondary", this.secondaryColor);
    this.style.setProperty("--chat-background-bubble", this.backgroundBubble);
    this.style.setProperty("--chat-background-chat", this.backgroundChat);
    this.classList.toggle("is-open", this.isOpen);
  }

  render() {
    const isEmpty = this.messages.length === 0;
    const isDarkMode = true;
    const instantMessages: string[] = JSON.parse(this.instantMessages);

    return html`
      <div class="chat-wrapper ${isDarkMode && "darkmode"}">
        ${this.isOpen
          ? html`
              <div class="chat-container ${this.closing ? "closing" : "open"}">
                ${this.chatState === "init"
                  ? html`
                      <!-- Estado 1: Inicio -->
                      <button class="chat-close-btn" @click=${this.toggleOpen}>
                        ${iconClose}
                      </button>
                      <div class="init">
                        <p class="welcome" .innerHTML=${this.welcome}></p>
                        <div class="welcome-img">
                          <img src=${this.avatarSrc} alt="Chatbot Avatar" />
                        </div>
                        <p class="welcome-message">${this.welcomeMessage}</p>
                        <button
                          class="welcome-button"
                          @click=${() => (this.chatState = "active")}
                        >
                          Hablemos ahora
                        </button>
                      </div>
                    `
                  : this.chatState === "active"
                  ? html`
                      <!-- Estado 2: Chat activo -->
                      <div class="container-margin">
                        <button
                          class="chat-close-btn active"
                          @click=${this.toggleOpen}
                        >
                          ${iconChevronDown}
                        </button>
                        <div
                          class="messages-header ${!isEmpty && "hasMessage"}"
                        >
                          <div class="message-welcome">
                            <p .innerHTML=${this.welcome}></p>
                            <div class="bot-messages">
                              <img src=${this.avatarSrc} alt="Chatbot Avatar" />
                            </div>
                          </div>
                          <span>${this.welcomeMessage}</span>
                        </div>
                        <div
                          class="messages ${this.messages.length === 0
                            ? "empty"
                            : ""}"
                        >
                          ${this.messages.map((msg) =>
                            msg.from === "bot"
                              ? html`
                                  <div class="message-wrapper bot">
                                    <img
                                      class="bot-avatar"
                                      src=${this.avatarSrc}
                                      alt="Bot avatar"
                                    />
                                    <div
                                      class="message bot"
                                      .innerHTML=${snarkdown(msg.text)}
                                    ></div>
                                  </div>
                                `
                              : msg.from == "error"
                              ? html`
                                  <div class="message-wrapper">
                                    <img
                                      class="bot-avatar"
                                      src=${this.avatarSrc}
                                      alt="Bot avatar"
                                    />
                                    <div
                                      class="message error"
                                      .innerHTML=${snarkdown(msg.text)}
                                    ></div>
                                  </div>
                                `
                              : html`
                                  <div class="message-wrapper user">
                                    <div
                                      class="message user"
                                      .innerHTML=${msg.text}
                                    ></div>
                                  </div>
                                `
                          )}
                          ${this.isLoading
                            ? html`
                                <div class="message-wrapper">
                                  <img
                                    class="bot-avatar-loading"
                                    src=${this.avatarSrc}
                                    alt="Bot avatar"
                                  />
                                  <div class="typing-message">
                                    ${this.textLoading}
                                  </div>
                                </div>
                              `
                            : null}

                          <div id="bottom-anchor"></div>
                        </div>
                        <div class="active-footer">
                          <div class="instant-messages-wrapper">
                            <button>${iconClose}</button>
                            <div class="instant-messages">
                              ${instantMessages.map(
                                (msg) =>
                                  html`<button
                                    @click=${() => {
                                      this.sendQuick(msg);
                                      this.resetTimeout();
                                    }}
                                    ?disabled=${this.isLoading}
                                  >
                                    ${msg}
                                  </button>`
                              )}
                            </div>
                          </div>
                          <div class="input-area">
                            <input
                              type="text"
                              placeholder="${this.placeholder}"
                              maxlength="90"
                              .value=${this.inputText}
                              @input=${(e: Event) => {
                                this.inputText = (
                                  e.target as HTMLInputElement
                                ).value;
                                this.resetTimeout();
                              }}
                              @keydown=${(e: KeyboardEvent) => {
                                if (e.key === "Enter") this.sendMessage();
                              }}
                              ?disabled=${this.isLoading}
                            />

                            <button @click=${this.sendMessage}>
                              ${iconSend}
                            </button>
                          </div>
                          <p class="credits">
                            Powered by <strong>GlobalsOne</strong> -
                            v${CURRENT_VERSION}
                          </p>
                        </div>
                      </div>
                    `
                  : html`
                      <!-- Estado 3: Timeout -->
                      <div class="container-margin timeout">
                        <div class="end-wrapper">
                          <p class="end-title">${this.endText}</p>
                          <p class="end-message">${this.endMessage}</p>
                          <ul>
                            ${this.phone
                              ? html`<li>
                                  <a href="tel:${this.phone}">${iconPhone}</a>
                                </li>`
                              : null}
                            ${this.email
                              ? html`<li>
                                  <a href="mailto:${this.email}"
                                    >${iconEmail}</a
                                  >
                                </li>`
                              : null}
                            ${this.whatsapp
                              ? html`
                                  <li>
                                    <a
                                      href="https://wa.me/${this.whatsapp}"
                                      target="_blank"
                                    >
                                      ${iconWhatsapp}
                                    </a>
                                  </li>
                                `
                              : null}
                          </ul>

                          <p
                            class="end-message-footer"
                            .innerHTML=${this.endMessageBottom}
                          ></p>
                          <button
                            class="end-button"
                            @click=${() => (this.chatState = "active")}
                          >
                            <img src=${this.avatarSrc} />
                            <span>${this.endButton}</span>
                          </button>
                        </div>
                      </div>
                    `}
              </div>
            `
          : null}

        <div class="chat-button" @click=${this.toggleOpen}>
          <img src=${this.avatarSrc} alt="Chatbot Avatar" />
        </div>
      </div>
    `;
  }
}
